import { UIColor } from "uicore-ts"
import { CBColor } from "../CB/CBColor"
import { BSColor } from "./BSColor"
import { RHColors } from "./Utils_slop/RHDesignTokens"


/**
 * RHColor
 *
 * The single theming source of truth for the application.
 * All default values are sourced from BSColor, but every color can be
 * overridden independently via updateSemanticColor("key", "#hex").
 */
export class RHColor extends UIColor {
    
    
    // --- Primary ---
    
    static readonly _primary = BSColor._primary
    
    private static get _primaryBase(): UIColor {
        return new UIColor(RHColor._primary)
    }
    
    static get primary(): UIColor {
        return RHColor._primaryBase.withSemanticKey("primary", RHColor)
    }
    
    static get primaryHover(): UIColor {
        return RHColor._primaryBase.colorByMultiplyingRGB(0.850).withSemanticKey("primaryHover", RHColor)
    }
    
    static get primaryActive(): UIColor {
        return RHColor._primaryBase.colorByMultiplyingRGB(0.800).withSemanticKey("primaryActive", RHColor)
    }
    
    static get primaryBorder(): UIColor {
        return RHColor._primaryBase.withSemanticKey("primaryBorder", RHColor)
    }
    
    static get primaryBorderHover(): UIColor {
        return RHColor._primaryBase.colorByMultiplyingRGB(0.800).withSemanticKey("primaryBorderHover", RHColor)
    }
    
    static get primaryBorderActive(): UIColor {
        return RHColor._primaryBase.colorByMultiplyingRGB(0.752).withSemanticKey("primaryBorderActive", RHColor)
    }
    
    static get primaryShadow(): UIColor {
        return RHColor._primaryBase.colorWithAlpha(0.5).withSemanticKey("primaryShadow", RHColor)
    }
    
    static get focusRing(): UIColor {
        return RHColor._primaryBase.colorWithAlpha(0.25).withSemanticKey("focusRing", RHColor)
    }
    
    
    // --- Secondary ---
    
    static readonly _secondary = BSColor._secondary
    
    static get secondary(): UIColor {
        return new UIColor(RHColor._secondary).withSemanticKey("secondary", RHColor)
    }
    
    static get secondaryHover(): UIColor {
        return new UIColor(BSColor._secondary).colorByMultiplyingRGB(0.850).withSemanticKey("secondaryHover", RHColor)
    }
    
    static get secondaryActive(): UIColor {
        return new UIColor(BSColor._secondary).colorByMultiplyingRGB(0.800).withSemanticKey("secondaryActive", RHColor)
    }
    
    static get secondaryBorder(): UIColor {
        return new UIColor(RHColor._secondary).withSemanticKey("secondaryBorder", RHColor)
    }
    
    static get secondaryBorderHover(): UIColor {
        return new UIColor(BSColor._secondary).colorByMultiplyingRGB(0.800).withSemanticKey("secondaryBorderHover", RHColor)
    }
    
    static get secondaryBorderActive(): UIColor {
        return new UIColor(BSColor._secondary).colorByMultiplyingRGB(0.752).withSemanticKey("secondaryBorderActive", RHColor)
    }
    
    static get secondaryShadow(): UIColor {
        return new UIColor(BSColor._secondary).colorWithAlpha(0.5).withSemanticKey("secondaryShadow", RHColor)
    }
    
    
    // --- Success ---
    
    static readonly _success = BSColor._success
    
    static get success(): UIColor {
        return new UIColor(RHColor._success).withSemanticKey("success", RHColor)
    }
    
    static get successHover(): UIColor {
        return new UIColor(RHColor._success).colorByMultiplyingRGB(0.850).withSemanticKey("successHover", RHColor)
    }
    
    static get successActive(): UIColor {
        return new UIColor(RHColor._success).colorByMultiplyingRGB(0.800).withSemanticKey("successActive", RHColor)
    }
    
    static get successBorder(): UIColor {
        return new UIColor(RHColor._success).withSemanticKey("successBorder", RHColor)
    }
    
    static get successBorderHover(): UIColor {
        return new UIColor(RHColor._success).colorByMultiplyingRGB(0.800).withSemanticKey("successBorderHover", RHColor)
    }
    
    static get successBorderActive(): UIColor {
        return new UIColor(RHColor._success).colorByMultiplyingRGB(0.752).withSemanticKey("successBorderActive", RHColor)
    }
    
    static get successShadow(): UIColor {
        return new UIColor(RHColor._success).colorWithAlpha(0.5).withSemanticKey("successShadow", RHColor)
    }
    
    
    // --- Danger ---
    
    static readonly _danger = BSColor._danger
    
    static get danger(): UIColor {
        return new UIColor(RHColor._danger).withSemanticKey("danger", RHColor)
    }
    
    static get dangerHover(): UIColor {
        return new UIColor(RHColor._danger).colorByMultiplyingRGB(0.850).withSemanticKey("dangerHover", RHColor)
    }
    
    static get dangerActive(): UIColor {
        return new UIColor(RHColor._danger).colorByMultiplyingRGB(0.800).withSemanticKey("dangerActive", RHColor)
    }
    
    static get dangerBorder(): UIColor {
        return new UIColor(RHColor._danger).withSemanticKey("dangerBorder", RHColor)
    }
    
    static get dangerBorderHover(): UIColor {
        return new UIColor(RHColor._danger).colorByMultiplyingRGB(0.800).withSemanticKey("dangerBorderHover", RHColor)
    }
    
    static get dangerBorderActive(): UIColor {
        return new UIColor(RHColor._danger).colorByMultiplyingRGB(0.752).withSemanticKey("dangerBorderActive", RHColor)
    }
    
    static get dangerShadow(): UIColor {
        return new UIColor(RHColor._danger).colorWithAlpha(0.5).withSemanticKey("dangerShadow", RHColor)
    }
    
    
    // --- Warning ---
    
    static readonly _warningOrange = BSColor._warningOrange
    
    static get warningOrange(): UIColor {
        return new UIColor(RHColor._warningOrange).withSemanticKey("warningOrange", RHColor)
    }
    
    static readonly _warning = BSColor._warning
    
    static get warning(): UIColor {
        return new UIColor(RHColor._warning).withSemanticKey("warning", RHColor)
    }
    
    static get warningHover(): UIColor {
        return new UIColor(RHColor._warning).colorByMultiplyingRGB(1.05).withSemanticKey("warningHover", RHColor)
    }
    
    static get warningActive(): UIColor {
        return new UIColor(RHColor._warning).colorByMultiplyingRGB(1.08).withSemanticKey("warningActive", RHColor)
    }
    
    static get warningBorder(): UIColor {
        return new UIColor(RHColor._warning).withSemanticKey("warningBorder", RHColor)
    }
    
    static get warningBorderHover(): UIColor {
        return new UIColor(RHColor._warning).colorByMultiplyingRGB(1.05).withSemanticKey("warningBorderHover", RHColor)
    }
    
    static get warningBorderActive(): UIColor {
        return new UIColor(RHColor._warning).colorByMultiplyingRGB(1.05).withSemanticKey("warningBorderActive", RHColor)
    }
    
    static get warningShadow(): UIColor {
        return new UIColor(RHColor._warning).colorWithAlpha(0.5).withSemanticKey("warningShadow", RHColor)
    }
    
    
    // --- Info ---
    
    static readonly _info = BSColor._info
    
    static get info(): UIColor {
        return new UIColor(RHColor._info).withSemanticKey("info", RHColor)
    }
    
    static get infoHover(): UIColor {
        return new UIColor(RHColor._info).colorByMultiplyingRGB(1.05).withSemanticKey("infoHover", RHColor)
    }
    
    static get infoActive(): UIColor {
        return new UIColor(RHColor._info).colorByMultiplyingRGB(1.08).withSemanticKey("infoActive", RHColor)
    }
    
    static get infoBorder(): UIColor {
        return new UIColor(RHColor._info).withSemanticKey("infoBorder", RHColor)
    }
    
    static get infoBorderHover(): UIColor {
        return new UIColor(RHColor._info).colorByMultiplyingRGB(1.05).withSemanticKey("infoBorderHover", RHColor)
    }
    
    static get infoBorderActive(): UIColor {
        return new UIColor(RHColor._info).colorByMultiplyingRGB(1.05).withSemanticKey("infoBorderActive", RHColor)
    }
    
    static get infoShadow(): UIColor {
        return new UIColor(RHColor._info).colorWithAlpha(0.5).withSemanticKey("infoShadow", RHColor)
    }
    
    
    // --- Light ---
    
    static readonly _light = BSColor._light
    
    static get light(): UIColor {
        return new UIColor(RHColor._light).withSemanticKey("light", RHColor)
    }
    
    static get lightHover(): UIColor {
        return new UIColor(RHColor._light).colorByMultiplyingRGB(0.920).withSemanticKey("lightHover", RHColor)
    }
    
    static get lightActive(): UIColor {
        return new UIColor(RHColor._light).colorByMultiplyingRGB(0.900).withSemanticKey("lightActive", RHColor)
    }
    
    static get lightBorder(): UIColor {
        return new UIColor(RHColor._light).withSemanticKey("lightBorder", RHColor)
    }
    
    static get lightBorderHover(): UIColor {
        return new UIColor(RHColor._light).colorByMultiplyingRGB(0.900).withSemanticKey("lightBorderHover", RHColor)
    }
    
    static get lightBorderActive(): UIColor {
        return new UIColor(RHColor._light).colorByMultiplyingRGB(0.880).withSemanticKey("lightBorderActive", RHColor)
    }
    
    static get lightShadow(): UIColor {
        return new UIColor(RHColor._light).colorWithAlpha(0.5).withSemanticKey("lightShadow", RHColor)
    }
    
    
    // --- Dark ---
    
    static readonly _dark = BSColor._dark
    
    static get dark(): UIColor {
        return new UIColor(RHColor._dark).withSemanticKey("dark", RHColor)
    }
    
    static get darkHover(): UIColor {
        return new UIColor(RHColor._dark).colorByMultiplyingRGB(1.800).withSemanticKey("darkHover", RHColor)
    }
    
    static get darkActive(): UIColor {
        return new UIColor(RHColor._dark).colorByMultiplyingRGB(2.000).withSemanticKey("darkActive", RHColor)
    }
    
    static get darkBorder(): UIColor {
        return new UIColor(RHColor._dark).withSemanticKey("darkBorder", RHColor)
    }
    
    static get darkBorderHover(): UIColor {
        return new UIColor(RHColor._dark).colorByMultiplyingRGB(1.700).withSemanticKey("darkBorderHover", RHColor)
    }
    
    static get darkBorderActive(): UIColor {
        return new UIColor(RHColor._dark).colorByMultiplyingRGB(1.700).withSemanticKey("darkBorderActive", RHColor)
    }
    
    static get darkShadow(): UIColor {
        return new UIColor(RHColor._dark).colorWithAlpha(0.5).withSemanticKey("darkShadow", RHColor)
    }
    
    
    // --- Text colors ---
    
    static readonly _textWhite = BSColor._textWhite
    
    static get textWhite(): UIColor {
        return new UIColor(RHColor._textWhite).withSemanticKey("textWhite", RHColor)
    }
    
    static readonly _textBlack = BSColor._textBlack
    
    static get textBlack(): UIColor {
        return new UIColor(RHColor._textBlack).withSemanticKey("textBlack", RHColor)
    }
    
    static readonly _textMuted = BSColor._textMuted
    
    static get textMuted(): UIColor {
        return new UIColor(RHColor._textMuted).withSemanticKey("textMuted", RHColor)
    }
    
    
    // --- Utility ---
    
    static readonly _disabled = BSColor._disabled
    
    static get disabled(): UIColor {
        return new UIColor(RHColor._disabled).withSemanticKey("disabled", RHColor)
    }
    
    static readonly _borderLight = BSColor._borderLight
    
    static get borderLight(): UIColor {
        return new UIColor(RHColor._borderLight).withSemanticKey("borderLight", RHColor)
    }
    
    static readonly _backgroundLight = BSColor._backgroundLight
    
    static get backgroundLight(): UIColor {
        return new UIColor(RHColor._backgroundLight).withSemanticKey("backgroundLight", RHColor)
    }
    
    
    // --- Application background ---
    
    static readonly _backgroundProcurer = "#fbfaf7"
    static readonly _backgroundOfferer = "#f9fafb"
    
    static get backgroundColor(): UIColor {
        return RHColors.pageBackground.withSemanticKey("backgroundColor", RHColor)
    }
    
    static get background() {
        return RHColors.pageBackground
    }
    
    
    // --- Application accent ---
    
    static readonly _callToActionColor = "#c94fa0"
    
    // The Purple color used for the CTA button
    // Extracted from: rgb(201, 79, 160) (The "Anna tagasisidet" button)
    static get callToActionColor(): UIColor {
        return new UIColor(RHColor._callToActionColor).withSemanticKey("callToActionColor", RHColor)
    }
    
    
    // --- Surface ---
    
    static readonly _surface = BSColor._backgroundLight
    
    static get surface(): UIColor {
        return new UIColor(RHColor._surface).withSemanticKey("surface", RHColor)
    }
    
    
    // --- Text ---
    
    static readonly _textPrimary = "#111827"
    
    static get textPrimary(): UIColor {
        return new UIColor(RHColor._textPrimary).withSemanticKey("textPrimary", RHColor)
    }
    
    static readonly _textSecondary = "#6b7280"
    
    static get textSecondary(): UIColor {
        return new UIColor(RHColor._textSecondary).withSemanticKey("textSecondary", RHColor)
    }
    
    
    // --- Borders ---
    
    static readonly _border = "#e5e7eb"
    
    static get border(): UIColor {
        return new UIColor(RHColor._border).withSemanticKey("border", RHColor)
    }
    
    static get borderFocus(): UIColor {
        return CBColor.primaryTintColor.withSemanticKey("borderFocus", RHColor)
    }
    
    
    // --- States ---
    
    static readonly _rowHover = "#f3f4f6"
    
    static get rowHover(): UIColor {
        return new UIColor(RHColor._rowHover).withSemanticKey("rowHover", RHColor)
    }
    
    static readonly _shadow = "rgba(0, 0, 0, 0.05)"
    
    static get shadow(): UIColor {
        return new UIColor(RHColor._shadow).withSemanticKey("shadow", RHColor)
    }
    
    
}
